import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { UpgradeContactModal, type ContactIntent } from './UpgradeContactModal.js';
import type { SubscriptionTier } from '../api/subscriptions.js';

/**
 * The upgrade call-to-action (ADR-0026 PR-3b-3). Self-serve tiers route to
 * /billing (Paddle checkout); Enterprise is sales-led → the contact modal
 * (checkout-context returns NOT_SELF_SERVE for it). Replaces the interim
 * "contact us to upgrade" modal at the three attach points.
 *
 * `tier` is the tenant's CURRENT tier: only `enterprise` takes the contact path;
 * everything else (incl. undefined, e.g. a seat-cap 402 where the tier isn't
 * loaded — never reachable on enterprise since it has no seat cap) routes to
 * /billing.
 *
 * `salesLed` forces the contact path regardless of the current tier — for a
 * plan gate whose REQUIRED tier is Enterprise (e.g. asset management,
 * multi-country payroll). Enterprise is not self-serve, so /billing can't sell
 * it; the caller (UpgradePrompt) sets this from the 402's `requiredTier`.
 *
 * `intent` only affects the contact modal's copy (ADR-0032 D9): `billing` for a
 * renewal/recovery CTA (past_due, read_only, cancelled), `upgrade` (default) for
 * a genuine upsell. The self-serve /billing link is unaffected.
 */
export function UpgradeCta({
  tier,
  label,
  salesLed = false,
  intent = 'upgrade',
  className,
}: {
  tier?: SubscriptionTier;
  /** Force the sales-led contact path (e.g. an Enterprise-required 402). */
  salesLed?: boolean;
  label: string;
  /** Contact-modal copy: a renewal/recovery (`billing`) or a real upsell (`upgrade`). */
  intent?: ContactIntent;
  className?: string;
}): JSX.Element {
  const [showContact, setShowContact] = useState(false);
  const cls = className ?? 'shrink-0 font-medium underline hover:no-underline';

  if (tier === 'enterprise' || salesLed) {
    return (
      <>
        <button type="button" onClick={() => setShowContact(true)} className={cls}>
          {label}
        </button>
        <UpgradeContactModal open={showContact} onClose={() => setShowContact(false)} intent={intent} />
      </>
    );
  }

  return (
    <Link to="/billing" className={cls}>
      {label}
    </Link>
  );
}
